import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { easing } from './lib/easing';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label``;

const Text = styled.span``;

const SwitchWrapper = styled.div`
  display: flex;
  gap: 12px;
  font-weight: var(--bold);
`;

const InputWrapper = styled.div`
  position: relative;
`;

const HiddenInput = styled.input`
  cursor: inherit;
  height: 100%;
  left: 0;
  margin: 0;
  opacity: 0;
  padding: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const SwitchInput = styled.div`
  display: flex;
  width: 32px;
  height: 16px;
  border-radius: 8px;
  position: relative;
  will-change: left, right;
  cursor: pointer;
  padding: 3px;
  outline: none;
  background-color: var(--switch-bg);
  z-index: 1;

  :hover,
  :focus {
    background-color: var(--switch-hover-bg);

    :after {
      background-color: var(--switch-dot-hover-bg);
    }
  }

  :after {
    transition: ${easing('transform')};
    content: '';
    height: 10px;
    width: 10px;
    border-radius: 50%;
    background-color: var(--switch-dot-bg);
  }

  :focus {
    :before {
      content: '';
      position: absolute;
      top: -4px;
      left: -4px;
      right: -4px;
      bottom: -4px;
      border: 2px solid var(--pleather);
      border-radius: 16px;
    }
  }

  :focus:not(:focus-visible) {
    :before {
      boder: none;
    }
  }

  &[aria-checked='true'] {
    background-color: var(--switch-checked-bg);

    :after {
      /* SwitchInput width - after width - SwitchInput padding = 16px*/
      transform: translateX(16px);
      background-color: var(--switch-dot-checked-bg);
    }

    :hover,
    :focus {
      background-color: var(--switch-checked-hover-bg);
      :after {
        background-color: var(--switch-dot-checked-hover-bg);
      }
    }
  }

  &[aria-disabled='true'] {
    background-color: var(--switch-disabled-bg);

    :after {
      background-color: var(--switch-dot-disabled-bg);
    }

    :hover,
    :focus {
      background-color: var(--switch-disabled-bg);
      :after {
        background-color: var(--switch-dot-disabled-bg);
      }
    }
  }

  &[aria-disabled='true'][aria-checked='true'] {
    background-color: var(--switch-disabled-checked-bg);

    :after {
      transform: translateX(16px);
      background-color: var(--switch-dot-disabled-checked-bg);
    }

    :hover,
    :focus {
      background-color: var(--switch-disabled-checked-bg);
      :after {
        background-color: var(--switch-dot-disabled-checked-bg);
      }
    }
  }
`;

function Switch({
  checked: initialValue = false,
  className,
  disabled,
  label,
  onChange,
  onKeyDown,
  textOff,
  textOn,
  ...unhandledProps
}) {
  const [checked, setChecked] = useState(initialValue);

  const { labelID, textOffID, textOnID } = useMemo(() => {
    const random = Number.parseInt(Math.random() * Number.MAX_SAFE_INTEGER, 10);

    return {
      labelID: `${random}-label-switch`,
      textOffID: `${random}-text-on-switch`,
      textOnID: `${random}-text-off-switch`,
    };
  }, []);

  useEffect(() => {
    setChecked(initialValue);
  }, [initialValue]);

  /*
   * NOTE: Due to a bug in React, the <input> onChange handler will still be
   *       called even after default has been prevented for the click handler.
   *       To workaround the double change handler, we check whether the native
   *       event was default prevented and prevent default after this
   *       short-circuit. [https://github.com/facebook/react/issues/9023]
   */
  const handleChange = useCallback(
    event => {
      if (disabled || event.nativeEvent.defaultPrevented) {
        return;
      }

      event.preventDefault();

      if (onChange) {
        onChange(!checked, event);
        event.persist();
      }

      setChecked(!checked);
    },
    [checked, disabled, onChange]
  );

  const handleKeyDown = useCallback(
    event => {
      if (event.key === ' ') {
        event.preventDefault();
        onChange?.(!checked, event);
        setChecked(isChecked => !isChecked);
      }
      onKeyDown?.(event);
    },
    [onKeyDown, onChange, checked]
  );

  return (
    <Wrapper>
      {label && <Label id={labelID}>{label}</Label>}
      <SwitchWrapper>
        {textOff && <Text id={textOffID}>{textOff}</Text>}
        <InputWrapper>
          <SwitchInput
            checked={checked}
            className={className}
            disabled={disabled}
            onClick={handleChange}
            aria-checked={checked}
            aria-disabled={disabled}
            onKeyDown={handleKeyDown}
            tabIndex={disabled ? -1 : 0}
            role="switch"
            aria-label="switch input"
            aria-labelledby={`${labelID} ${textOffID} ${textOnID}`}
          />
          <HiddenInput
            aria-hidden="true"
            checked={checked}
            disabled={disabled}
            onChange={handleChange}
            type="checkbox"
            tabIndex="-1"
            {...unhandledProps}
          />
        </InputWrapper>
        {textOn && <Text id={textOnID}>{textOn}</Text>}
      </SwitchWrapper>
    </Wrapper>
  );
}

Switch.propTypes = {
  checked: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  textOff: PropTypes.string,
  textOn: PropTypes.string,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
};

export default styled(Switch)``;
